import { useState, useEffect, useCallback, useRef } from 'react';

export default function useCollaboration(mapId, sessionToken, isOwner = false) {
  const [participants, setParticipants] = useState([]);
  const [cursors, setCursors] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [remoteNodeDrag, setRemoteNodeDrag] = useState(null); // {nodeId, position}
  const [remoteTextEdit, setRemoteTextEdit] = useState(null); // {nodeId, label}
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  // Connect to WebSocket
  useEffect(() => {
    if (!mapId || !sessionToken) return;

    const connect = () => {
      // Use the backend URL for WebSocket, replacing http with ws
      // IMPORTANT: Use /api prefix for proper K8s ingress routing
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const wsProtocol = backendUrl.startsWith('https') ? 'wss:' : 'ws:';
      const wsHost = backendUrl.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}//${wsHost}/api/ws/collab/${mapId}`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('WebSocket connected, sending auth...');
          // Send auth message
          ws.send(JSON.stringify({
            type: 'auth',
            session_token: sessionToken,
            is_owner: isOwner
          }));
          setIsConnected(true);
          reconnectAttempts.current = 0;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            switch (data.type) {
              case 'participants':
                setParticipants(data.participants || []);
                break;
              
              case 'user_joined':
                setParticipants(prev => {
                  const exists = prev.some(p => p.session_id === data.session_id);
                  if (exists) return prev;
                  return [...prev, {
                    session_id: data.session_id,
                    username: data.username,
                    cursor_color: data.cursor_color,
                    is_owner: data.is_owner
                  }];
                });
                break;
              
              case 'user_left':
                setParticipants(prev => prev.filter(p => p.session_id !== data.session_id));
                setCursors(prev => {
                  const next = { ...prev };
                  delete next[data.session_id];
                  return next;
                });
                break;
              
              case 'cursor_move':
                setCursors(prev => ({
                  ...prev,
                  [data.session_id]: {
                    x: data.x,
                    y: data.y,
                    username: data.username,
                    color: data.cursor_color
                  }
                }));
                break;
              
              case 'node_drag':
                // Real-time node position update during drag
                console.log('useCollaboration: Received node_drag', data.node_id, data.position);
                setRemoteNodeDrag({
                  nodeId: data.node_id,
                  position: data.position,
                  sessionId: data.session_id,
                  timestamp: Date.now() // Ensure React detects the change
                });
                break;
              
              case 'node_drag_end':
                // Node drag finished - clear remote drag state
                setRemoteNodeDrag(null);
                // Also trigger full map update
                if (window.__collabMapUpdate && data.nodes && data.edges) {
                  window.__collabMapUpdate(data.nodes, data.edges);
                }
                break;
              
              case 'text_edit':
                // Real-time text editing
                setRemoteTextEdit({
                  nodeId: data.node_id,
                  label: data.label,
                  sessionId: data.session_id,
                  timestamp: Date.now() // Ensure React detects the change
                });
                break;
              
              case 'map_update':
                // Full map sync - handled by parent component via global callback
                if (window.__collabMapUpdate) {
                  window.__collabMapUpdate(data.nodes, data.edges);
                }
                break;
              
              case 'node_select':
                // Can be used to show which node others are selecting
                break;
                
              default:
                console.log('Unknown message type:', data.type);
            }
          } catch (err) {
            console.error('Error parsing WebSocket message:', err);
          }
        };

        ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          setIsConnected(false);
          
          // Reconnect with exponential backoff
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          
          if (reconnectAttempts.current < 10) {
            console.log(`Reconnecting in ${delay}ms...`);
            reconnectTimeoutRef.current = setTimeout(connect, delay);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
      } catch (err) {
        console.error('Failed to create WebSocket:', err);
      }
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [mapId, sessionToken, isOwner]);

  // Send cursor position
  const sendCursorPosition = useCallback((x, y) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cursor_move',
        x,
        y
      }));
    }
  }, []);

  // Send node drag position (during drag - high frequency)
  const sendNodeDrag = useCallback((nodeId, position) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'node_drag',
        node_id: nodeId,
        position
      }));
    }
  }, []);

  // Send node drag end (when drag stops - includes full state for persistence)
  const sendNodeDragEnd = useCallback((nodes, edges) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'node_drag_end',
        nodes,
        edges
      }));
    }
  }, []);

  // Send text edit (during typing - high frequency)
  const sendTextEdit = useCallback((nodeId, label) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'text_edit',
        node_id: nodeId,
        label
      }));
    }
  }, []);

  // Send map update (full sync)
  const sendMapUpdate = useCallback((nodes, edges) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'map_update',
        nodes,
        edges
      }));
    }
  }, []);

  // Send node selection
  const sendNodeSelect = useCallback((nodeId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'node_select',
        node_id: nodeId
      }));
    }
  }, []);

  return {
    participants,
    cursors,
    isConnected,
    remoteNodeDrag,
    remoteTextEdit,
    sendCursorPosition,
    sendNodeDrag,
    sendNodeDragEnd,
    sendTextEdit,
    sendMapUpdate,
    sendNodeSelect
  };
}
